<template>
  <div class="add-fee__page">
    <div class="d-flex align-center justify-space-between my-4">
      <h2>Ads Fee</h2>
      <div class="d-flex">
        <v-btn color="secondary" outlined @click="makeOwner" :loading="ownerLoading" class="mr-2">
          <v-icon left>mdi-account-star</v-icon>
          Mark as Owner
        </v-btn>
        <v-btn color="primary" @click="openCreateDialog">
          <v-icon left>mdi-plus</v-icon>
          Create New Fee
        </v-btn>
      </div>
    </div>

    <v-data-table
      :loading="table.loading"
      :headers="table.columns"
      :items="table.records"
      :items-per-page="-1"
      item-key="id"
      no-data-text="No fee records available"
      hide-default-footer
    >
      <template v-slot:[`item.date`]="{ item }">
        {{ formatDate(item.date) }}
      </template>

      <template v-slot:[`item.fee`]="{ item }">
        {{ formatFee(item.ads_fee) }}
      </template>

      <template v-slot:[`item.status`]="{ item }">
        <v-chip :color="getStatusColor(item.status_fee)" label small class="text-capitalize">
          {{ formatStatus(item.status_fee) }}
        </v-chip>
      </template>
      <template v-slot:[`item.details`]="{ item }">
        <div v-if="item.details && item.details.length > 0">
          <div
            v-for="detail in visibleDetails(item)"
            :key="detail._id"
            style="
              padding: 6px 8px;
              border: 1px solid rgba(0, 0, 0, 0.08);
              border-radius: 8px;
              background: #fff;
              margin-bottom: 6px;
            "
          >
            <div v-if="isEditingDetail(detail)">
              <div
                style="
                  font-weight: 600;
                  font-size: 12px;
                  word-break: break-all;
                  text-align: left;
                "
              >
                {{ detail.accountId }}
              </div>
              <div class="d-flex align-center justify-space-between" style="gap: 12px;">
                <v-text-field
                  v-model.number="editingDetailValue"
                  type="number"
                  min="0"
                  step="0.01"
                  prefix="$"
                  dense
                  hide-details
                  autofocus
                  placeholder="0.00"
                  @keyup.enter="saveEditDetail(detail)"
                  @keyup.esc="cancelEditDetail"
                  class="custom-form-input-text"
                />
                <div class="d-flex align-center" style="gap: 4px;">
                  <v-btn icon class="custom-btn" @click="cancelEditDetail">
                    <v-icon size="18">mdi-close</v-icon>
                  </v-btn>
                  <v-btn
                    class="custom-btn"
                    color="primary"
                    :loading="loadingEditDetail"
                    @click="saveEditDetail(detail)"
                  >
                    <v-icon size="18">mdi-check</v-icon>
                  </v-btn>
                </div>
              </div>
            </div>

            <div v-else class="d-flex align-center justify-space-between" style="gap: 12px;">
              <div style="font-weight: 600; font-size: 13px; color: rgba(0, 0, 0, 0.75); word-break: break-all;">
                {{ detail.accountId }}
              </div>
              <div class="d-flex align-center" style="gap: 6px; white-space: nowrap;">
                <span>{{ formatFee(detail.ads_fee) }}</span>
                <v-btn
                  class="custom-btn"
                  icon
                  color="primary"
                  @click="startEditDetail(detail)"
                  :disabled="table.loading || loadingEditDetail"
                >
                  <v-icon size="18">mdi-pencil</v-icon>
                </v-btn>
              </div>
            </div>
          </div>

          <div v-if="detailsCount(item) > 1" style="margin-left: -4px;" class="mt-1">
            <v-btn text small color="primary" class="px-1" @click="toggleDetails(item)">
              {{ isDetailsExpanded(item) ? 'Show less' : `Show more (${detailsCount(item) - 1})` }}
            </v-btn>
          </div>
        </div>
        <div v-else class="text--secondary">No data</div>
      </template>
    </v-data-table>

    <!-- Pagination -->
    <div class="d-flex justify-end mt-4" v-if="table.records && table.records.length > 0">
      <v-pagination
        :length="pagination.total"
        v-model="pagination.page"
        prev-icon="mdi-menu-left"
        next-icon="mdi-menu-right"
        :total-visible="5"
      ></v-pagination>
    </div>

    <!-- Create Fee Dialog -->
    <v-dialog v-model="createDialog.show" max-width="500px">
      <v-card>
        <p style="font-size: 20px; padding-top: 12px; padding-left: 12px;">Create New Ads Fee</p>
        <v-card-text>
          <v-form ref="createForm">
            <v-row>
              <v-col cols="12" class="form-component pb-0">
                <label>Date <span style="color: red">*</span></label>
                <v-menu
                  ref="dateMenuRef"
                  v-model="createDialog.dateMenu"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y
                  max-width="290px"
                  min-width="auto"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-model="createDialog.date"
                      prepend-inner-icon="mdi-calendar"
                      readonly
                      v-bind="attrs"
                      v-on="on"
                      :rules="[v => !!v || 'Date is required']"
                    ></v-text-field>
                  </template>
                  <v-date-picker
                    v-model="createDialog.date"
                    :max="new Date().toISOString().substr(0, 10)"
                    no-title
                    scrollable
                  >
                    <v-spacer></v-spacer>
                    <v-btn text color="primary" @click="createDialog.dateMenu = false">Cancel</v-btn>
                    <v-btn
                      text
                      color="primary"
                      @click="
                        $refs.dateMenuRef.save(createDialog.date);
                        createDialog.dateMenu = false;
                      "
                    >
                      OK
                    </v-btn>
                  </v-date-picker>
                </v-menu>
              </v-col>
              <v-col cols="12" class="form-component pb-0">
                <label>Account <span style="color: red">*</span></label>
                <AvailableAccountSelector
                  v-model="createDialog.accountIdSelected"
                  :rules="[]"
                  :autoFetch="false"
                  :hideDetails="true"
                  class="mb-2"
                  @input="onAccountSelected"
                />
                <div class="text-center mt-0 mb-2 text--secondary">or</div>
                <v-text-field
                  v-model="createDialog.accountIdText"
                  type="text"
                  placeholder="Enter Account ID"
                  hide-details
                  class="custom-form-account"
                  @input="onAccountTextChanged"
                />
                <v-messages :value="createDialog.accountErrors" color="error" />
              </v-col>
              <v-col cols="12" class="form-component pb-0">
                <label>Ads Fee <span style="color: red">*</span></label>
                <v-text-field
                  v-model.number="createDialog.adsFee"
                  type="number"
                  min="0"
                  step="0.01"
                  :rules="[v => v >= 0 || 'Fee must be greater than or equal to 0']"
                  prefix="$"
                  class="custom-form-input-text"
                ></v-text-field>
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="closeCreateDialog">Cancel</v-btn>
          <v-btn color="primary" @click="createFee" :loading="createDialog.loading">Create</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Confirm Owner Dialog -->
    <v-dialog v-model="confirmOwnerDialog.show" max-width="500px">
      <v-card>
        <p style="font-size: 20px; padding-top: 12px; padding-left: 12px;">Confirm Mark as Owner</p>
        <v-card-text>
          <p>Are you sure you want to mark this account as the owner of this store?</p>
          <p class="text--secondary mt-2">This action will designate the current account as the store owner.</p>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="confirmOwnerDialog.show = false">Cancel</v-btn>
          <v-btn color="primary" @click="confirmMakeOwner" :loading="ownerLoading">Confirm</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import moment from 'moment';
import { addFeeApi } from '@/apis/addFee';
import AvailableAccountSelector from './components/AvailableAccountSelector.vue';

export default {
  components: {
    AvailableAccountSelector,
  },
  data() {
    return {
      table: {
        loading: false,
        columns: [
          { text: 'Date', value: 'date', sortable: false, width: '30%' },
          { text: 'Ads Fee', value: 'fee', sortable: false, width: '20%' },
          { text: 'Ads Fee By Accounts', value: 'details', sortable: false, width: '30%' },
          { text: 'Status', value: 'status', sortable: false, width: '20%' },
        ],
        records: [],
      },
      pagination: {
        page: 1,
        limit: 20,
        total: 1,
      },
      ownerLoading: false,
      createDialog: {
        show: false,
        loading: false,
        dateMenu: false,
        date: null,
        adsFee: 0,
        accountIdSelected: null,
        accountIdText: '',
        accountErrors: [],
      },
      confirmOwnerDialog: {
        show: false,
      },
      detailsExpandedKeySet: {},
      loadingEditDetail: false,
      editingDetailRowKey: null,
      editingDetailValue: null,
    };
  },
  computed: {
    createAccountId() {
      const selected = (this.createDialog.accountIdSelected || '').trim();
      if (selected) return selected;

      const typed = (this.createDialog.accountIdText || '').trim();
      return typed || null;
    },
  },
  methods: {
    onAccountSelected(next) {
      this.createDialog.accountErrors = [];
      const hasSelected = !!(next || '').toString().trim();
      if (hasSelected) {
        this.createDialog.accountIdText = '';
      }
    },
    onAccountTextChanged(next) {
      this.createDialog.accountErrors = [];
      const raw = (next || '').toString();
      const sanitized = raw.replace(/[^a-zA-Z0-9]/g, '');
      if (raw !== sanitized) {
        this.createDialog.accountIdText = sanitized;
        this.createDialog.accountErrors = ['Account ID must contain only letters and numbers'];
      }

      const hasTyped = !!sanitized.trim();
      if (hasTyped) {
        this.createDialog.accountIdSelected = null;
      }
    },
    formatDate(date) {
      return moment(date).format('DD/MM/YYYY');
    },
    formatFee(fee) {
      if (!fee && fee !== 0) return '$0.00';
      const numFee = typeof fee === 'string' ? parseFloat(fee) : fee;
      return isNaN(numFee) ? '$0.00' : `$${numFee.toFixed(2)}`;
    },
    formatStatus(status) {
      if (!status) return 'N/A';
      return status.replace(/_/g, ' ');
    },
    getStatusColor(status) {
      const statusColors = {
        pending: 'warning',
        approved: 'success',
      };
      return statusColors[status] || 'default';
    },
    detailsKey(item) {
      return item?.date || item?._id || item?.id || JSON.stringify(item);
    },
    detailsCount(item) {
      return Array.isArray(item?.details) ? item.details.length : 0;
    },
    isDetailsExpanded(item) {
      const key = this.detailsKey(item);
      return !!this.detailsExpandedKeySet[key];
    },
    toggleDetails(item) {
      const key = this.detailsKey(item);
      this.$set(this.detailsExpandedKeySet, key, !this.detailsExpandedKeySet[key]);
    },
    visibleDetails(item) {
      const details = Array.isArray(item?.details) ? item.details : [];
      if (details.length <= 1) return details;
      return this.isDetailsExpanded(item) ? details : details.slice(0, 1);
    },
    getDetailRowKey(detail) {
      if (detail && detail._id) return `id:${detail._id}`;
      return null;
    },
    isEditingDetail(detail) {
      return this.editingDetailRowKey === this.getDetailRowKey(detail);
    },
    startEditDetail(detail) {
      this.editingDetailRowKey = this.getDetailRowKey(detail);
      this.editingDetailValue = detail.ads_fee;
    },
    cancelEditDetail() {
      this.editingDetailRowKey = null;
      this.editingDetailValue = null;
    },
    async saveEditDetail(detail) {
      try {
        this.loadingEditDetail = true;
        await addFeeApi.updateFeeDetail(detail._id, { adsFee: this.editingDetailValue });
        detail.ads_fee = this.editingDetailValue;
        this.cancelEditDetail();
        await this.getData();
        this.$store.commit('setMessages', {
          messages: 'Ads fee updated successfully!',
          type: 'success',
        });
      } catch (error) {
        console.error('Error saving ads fee:', error);
        this.$store.commit('setMessages', {
          messages: error.response?.data?.message || 'Failed to update ads fee',
          type: 'error',
        });
      } finally {
        this.loadingEditDetail = false;
      }
    },
    makeOwner() {
      this.confirmOwnerDialog.show = true;
    },
    async confirmMakeOwner() {
      try {
        this.ownerLoading = true;
        await addFeeApi.makeOwner();
        this.confirmOwnerDialog.show = false;
        this.$store.commit('setMessages', {
          messages: 'Successfully marked as store owner',
          type: 'success',
        });
      } catch (error) {
        console.error('Error making owner:', error);
        this.$store.commit('setMessages', {
          messages: error.response?.data?.message || 'Failed to mark as owner',
          type: 'error',
        });
      } finally {
        this.ownerLoading = false;
      }
    },
    async getData() {
      try {
        this.table.loading = true;

        const query = {
          limit: this.pagination.limit,
          page: this.pagination.page,
        };

        const { data } = await addFeeApi.getFees(query);

        // Lấy items từ response
        if (data && Array.isArray(data.items)) {
          this.table.records = data.items;
        } else {
          this.table.records = [];
        }

        // Cập nhật pagination từ response
        if (data && data.pagination) {
          this.pagination.total = data.pagination.totalPages || 1;
        }
      } catch (error) {
        console.error('Error fetching fees:', error);
        this.$store.commit('setMessages', {
          messages: error.response?.data?.message || 'Failed to load fee data',
          type: 'error',
        });
        this.table.records = []; // Đảm bảo records là array khi có lỗi
      } finally {
        this.table.loading = false;
      }
    },
    openCreateDialog() {
      this.createDialog.show = true;
      this.createDialog.date = moment().format('YYYY-MM-DD');
      this.createDialog.adsFee = 0;
      this.createDialog.accountIdSelected = null;
      this.createDialog.accountIdText = '';
      this.createDialog.accountErrors = [];
    },
    closeCreateDialog() {
      this.createDialog.show = false;
      this.createDialog.date = null;
      this.createDialog.adsFee = 0;
      this.createDialog.accountIdSelected = null;
      this.createDialog.accountIdText = '';
      this.createDialog.accountErrors = [];
      if (this.$refs.createForm) {
        this.$refs.createForm.resetValidation();
      }
    },
    async createFee() {
      const formOk = this.$refs.createForm.validate();
      const selected = (this.createDialog.accountIdSelected || '').trim();
      const typed = (this.createDialog.accountIdText || '').trim();
      const bothFilled = !!selected && !!typed;
      const typedValid = !typed || /^[a-zA-Z0-9]+$/.test(typed);
      const validAccount = !!this.createAccountId && !bothFilled;

      if (bothFilled) {
        this.createDialog.accountErrors = ['Please fill either Account select OR Account text (not both).'];
      } else if (!typedValid) {
        this.createDialog.accountErrors = ['Account ID must contain only letters and numbers'];
      } else {
        this.createDialog.accountErrors = validAccount ? [] : ['Account is required'];
      }

      if (!formOk || !validAccount || !typedValid) {
        return;
      }

      try {
        this.createDialog.loading = true;

        const params = {
          adsFee: this.createDialog.adsFee,
          date: this.createDialog.date,
          accountId: this.createAccountId,
        };

        await addFeeApi.createFeeDetail(params);

        this.$store.commit('setMessages', {
          messages: 'Fee created successfully',
          type: 'success',
        });
        this.closeCreateDialog();
        await this.getData();
      } catch (error) {
        console.error('Error creating fee:', error);
        // Lấy message từ response API
        const errorMessage = error.response?.data?.message || error.message || 'Failed to create fee';
        this.$store.commit('setMessages', {
          messages: errorMessage,
          type: 'error',
        });
        // Không đóng dialog khi có lỗi để user có thể sửa
      } finally {
        this.createDialog.loading = false;
      }
    },
  },
  created() {
    this.getData();
  },
  watch: {
    'pagination.page': function() {
      this.getData();
    },
  },
};
</script>

<style scoped>
h2 {
  font-size: 32px;
}

.v-btn--outlined {
  border: solid 1px lightgray;
  border-radius: 8px;
}

tbody tr:nth-of-type(even) {
  background-color: #fafafa;
}

.theme--light.v-data-table thead tr th {
  color: black !important;
  font-weight: bold;
  font-size: 14px;
  text-transform: uppercase;
}

tbody tr.v-data-table__empty-wrapper td {
  font-size: 20px !important;
  font-weight: 600;
  padding-block: 32px !important;
}

.v-data-table >>> tbody tr td {
  padding: 12px 16px !important;
}

.action-btn {
  width: 20px !important;
  height: 20px !important;
  font-weight: bold !important;
  padding: 0px !important;
  min-width: 20px !important;
}

.action-btn .v-icon {
  font-size: 20px;
  font-weight: bold !important;
}

.custom-btn {
  padding: 0 !important;
  min-width: 28px !important;
  width: 28px !important;
  height: 28px !important;
  border-radius: 50% !important;
}

::v-deep .custom-form-input-text .v-text-field__prefix {
  padding-left: 12px !important;
}

::v-deep .v-messages__wrapper {
  margin-top: 4px !important;
}
</style>
